/**
 * word-context — reads the user's current focus in Word (selected text, or the
 * document body when nothing is selected) and exposes it as a HostContextProvider
 * so the assistant knows what "this" / "here" refers to. Mirrors the read
 * patterns in word-tools.ts (get_selected_text / get_document_text).
 */
import { HostContextProvider, capText } from "@usable/office-embed";

const MAX_CONTEXT_CHARS = 4000;

async function read(): Promise<string | null> {
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load("text");
      const body = context.document.body;
      body.load("text");
      await context.sync();

      const selected = (selection.text ?? "").trim();
      const lines = ["The user is working in Microsoft Word."];
      if (selected) {
        lines.push(`Current selection (${selected.length} chars):`, selected);
      } else {
        lines.push("No text is selected. Full document text:", body.text ?? "");
      }
      return capText(lines.join("\n"), MAX_CONTEXT_CHARS);
    });
  } catch {
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, onChange);
  return () => {
    Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
      handler: onChange,
    });
  };
}

export const wordContextProvider: HostContextProvider = { read, subscribe };
